import { Injectable } from '@angular/core';

// import {CrudService} from '@dash/randy/dashboard/services/crud.service';
// import {FileManagerModel} from '../classes/file-manager.model';
//
// @Injectable({
//     providedIn: 'root'
// })
// export class FileManagerService extends CrudService<FileManagerModel> {
//     constructor() {
//         super('fileManager');
//     }
// }
import { FileManagerItem, FileSearchInput, FileSearchResponse } from '../classes/file-manager.model';
import { DBCrudService, httpOptions } from '@dash/randy/dashboard/services/db-crud.service';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthorizationService } from '@dash/randy/security/service/authorization.service';
import { firstValueFrom, map, Observable, timeout } from 'rxjs';

export const httpOptionsForm = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class FileManagerItemService extends DBCrudService<FileManagerItem> {
  private readonly uploadedItemCache = new Map<string, FileManagerItem>();
  private readonly uploadPollingCache = new Map<string, Promise<FileManagerItem | null>>();
  private readonly uploadPollingCooldownUntil = new Map<string, number>();

  constructor(
    protected router: Router,
    protected http: HttpClient,
    protected authorizationService: AuthorizationService,
  ) {
    super('files', router, http, authorizationService);
  }

  override deleteObject(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.apiUrl}/${id}`, httpOptions).pipe(timeout(30_000)));
  }

  override updateObject(item: FileManagerItem): Promise<FileManagerItem> {
    return firstValueFrom(
      this.http
        .put<FileManagerItem>(`${this.apiUrl}/${item.id}`, JSON.stringify(item), httpOptions)
        .pipe(timeout(30_000)),
    );
  }

  search(input: FileSearchInput): Observable<FileSearchResponse> {
    return this.http.post<FileSearchResponse>(`${this.apiUrl}/search`, input, httpOptions).pipe(
      timeout(30_000),
      map(response => ({
        ...response,
        folders: response.folders.map(folder => ({ ...folder, isFolder: () => true })),
        files: response.files.map(file => ({ ...file, isFolder: () => false })),
      })),
    );
  }

  async unpackArchive(fileId: string, folderId: string, overwriteFiles = true, createFolders = true) {
    let data = { fileId, folderId, overwriteFiles, createFolders };
    return firstValueFrom(this.http.post<object>(`${this.apiUrl}/extract`, data, httpOptionsForm));
  }

  startRemoteTransfer(
    folderId: string,
    remote: {
      protocol: 'ftp' | 'ftps' | 'sftp';
      host: string;
      username: string;
      password: string;
      remotePath: string;
    },
  ) {
    const data = {
      folderId: folderId,
      remote: remote,
      overwriteFiles: false,
    };
    return firstValueFrom(this.http.post<{ success: boolean }>(`${this.apiUrl}/extract`, data, httpOptionsForm));
  }

  waitForUploadedObject(
    fileId: string,
    retryCount = 3,
    delayMs = 5000,
    cooldownMs = 60000,
  ): Promise<FileManagerItem | null> {
    const cachedItem = this.uploadedItemCache.get(fileId);
    if (cachedItem) {
      return Promise.resolve(cachedItem);
    }

    const cooldownUntil = this.uploadPollingCooldownUntil.get(fileId);
    if (cooldownUntil && cooldownUntil > Date.now()) {
      return Promise.resolve(null);
    }

    const existingPoll = this.uploadPollingCache.get(fileId);
    if (existingPoll) {
      return existingPoll;
    }

    const poll = this.pollForUploadedObject(fileId, retryCount, delayMs, cooldownMs).finally(() => {
      this.uploadPollingCache.delete(fileId);
    });
    this.uploadPollingCache.set(fileId, poll);
    return poll;
  }

  private async pollForUploadedObject(
    fileId: string,
    retryCount: number,
    delayMs: number,
    cooldownMs: number,
  ): Promise<FileManagerItem | null> {
    for (let i = 0; i < retryCount; i++) {
      await new Promise(resolve => setTimeout(resolve, delayMs));

      const item = await this.getObject(fileId);
      if (item.uploadedAt) {
        this.uploadedItemCache.set(fileId, item);
        this.uploadPollingCooldownUntil.delete(fileId);
        return item;
      }
    }

    this.uploadPollingCooldownUntil.set(fileId, Date.now() + cooldownMs);
    return null;
  }
}

export interface CheckinInput {
  name: string;
  extension: string;
  folderId?: string;
  path?: string;
  operationId?: string;
  mimeType?: string;
  mode?: 'single' | 'multipart'; // optional, default is 'single'
  checksumAlgorithm?: ChecksumAlgorithm;
  checksum?: string;
}

export type ChecksumAlgorithm = 'SHA256' | 'SHA1' | 'CRC32' | 'CRC32C' | 'CRC64NVME';

export interface CheckinOutput {
  id: string;
  applicationId: string;
  storageClass: string;
}

export interface CheckinOutputSingle extends CheckinOutput {
  presignedUrl: string;
  uploadHeaders: Record<string, string>;
}

export interface CheckinOutputMultipart extends CheckinOutput {
  uploadId: string;
}
